/**
 * Run service for lab execution management
 */

import { 
  Run, 
  RunWithDetails, 
  RunCreate, 
  RunExtend, 
  RunStats, 
  GradingJobStatus, 
  Grade,
  RunInstance 
} from '../types';
import { apiClient as api } from './api';

export class RunService {
  private baseUrl = '/runs';
  private collectionUrl = '/runs/';

  /**
   * Create a new lab run
   */
  async createRun(data: RunCreate): Promise<Run> {
    return await api.post<Run>(this.collectionUrl, data);
  }

  /**
   * Get all runs for current user with updated filtering terminology
   */
  async getRuns(filterState?: 'all' | 'active' | 'graded' | 'destroyed'): Promise<RunWithDetails[]> {
    const params = filterState ? { filter_state: filterState } : {};
    return await api.get<RunWithDetails[]>(this.collectionUrl, params);
  }

  /**
   * Get specific run by ID
   */
  async getRun(runId: number): Promise<RunWithDetails> {
    return await api.get<RunWithDetails>(`${this.baseUrl}/${runId}`);
  }

  /**
   * Get all VM instances for a run (supports multi-VM labs)
   */
  async getRunInstances(runId: number): Promise<RunInstance[]> {
    return await api.get<RunInstance[]>(`${this.baseUrl}/${runId}/instances`);
  }

  /**
   * Extend run TTL
   */
  async extendTTL(runId: number, extension: RunExtend): Promise<Run> {
    return await api.post<Run>(`${this.baseUrl}/${runId}/extend`, extension);
  }

  /**
   * Destroy a run
   */
  async destroyRun(runId: number): Promise<void> {
    await api.delete(`${this.baseUrl}/${runId}`);
  }

  /**
   * Start grading process
   */
  async gradeRun(runId: number): Promise<GradingJobStatus> {
    return await api.post<GradingJobStatus>(`${this.baseUrl}/${runId}/grade`);
  }

  /**
   * Get grade report
   */
  async getGradeReport(runId: number): Promise<Grade> {
    return await api.get<Grade>(`${this.baseUrl}/${runId}/report`);
  }

  /**
   * Get run statistics
   */
  async getRunStats(): Promise<RunStats> {
    return await api.get<RunStats>(`${this.baseUrl}/stats/summary`);
  }

  /**
   * Get user's grade history for improvement tracking
   */
  async getGradeHistory(): Promise<Grade[]> {
    return await api.get<Grade[]>(`${this.baseUrl}/grades/history`);
  }

  /**
   * Poll run state until it reaches a target state or timeout
   */
  async pollRunState(
    runId: number, 
    targetStates: string[], 
    timeoutMs = 300000, // 5 minutes
    intervalMs = 2000 // 2 seconds
  ): Promise<RunWithDetails> {
    const startTime = Date.now();
    
    while (Date.now() - startTime < timeoutMs) {
      const run = await this.getRun(runId);
      
      if (targetStates.some(state => state === run.state)) {
        return run;
      }
      
      if (run.state === 'failed') {
        throw new Error(`Run ${runId} failed during provisioning`);
      }
      
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    
    throw new Error(`Timeout waiting for run ${runId} to reach target state`);
  }

  /**
   * Format time remaining as human-readable string
   */
  formatTimeRemaining(ttlExpiresAt: string): string {
    const expiryTime = new Date(ttlExpiresAt);
    const now = new Date();
    const diffMs = expiryTime.getTime() - now.getTime();
    
    if (diffMs <= 0) {
      return 'Expired';
    }
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }

  /**
   * Get status color for UI display
   */
  getStatusColor(state: string): 'pending' | 'processing' | 'success' | 'error' | 'warning' {
    switch (state) {
      case 'pending':
        return 'pending';
      case 'provisioning':
        return 'processing';
      case 'active':
        return 'success';
      case 'destroying':
        return 'warning';
      case 'destroyed':
        return 'pending';
      case 'failed':
        return 'error';
      default:
        return 'pending';
    }
  }

  /**
   * Get human-readable status text with updated terminology
   */
  getStatusText(state: string): string {
    switch (state) {
      case 'pending':
        return 'Queued';
      case 'provisioning':
        return 'Starting up...';
      case 'active':
        return 'Running';
      case 'destroying':
        return 'Destroying...'; // Updated terminology
      case 'destroyed':
        return 'Destroyed'; // Changed from 'Stopped'
      case 'failed':
        return 'Failed';
      default:
        return 'Unknown';
    }
  }

  /**
   * Check if run can be destroyed
   */
  canDestroy(state: string): boolean {
    return !['destroying', 'destroyed'].some(value => value === state);
  }

  /**
   * Check if run can be graded
   */
  canGrade(state: string): boolean {
    return state === 'active';
  }

  /**
   * Check if run can have TTL extended
   */
  canExtendTTL(state: string): boolean {
    return state === 'active';
  }

  /**
   * Check if SSH access is available
   */
  canSSH(state: string): boolean {
    return state === 'active';
  }

  /**
   * Generate SSH command for connecting to run
   */
  getSSHCommand(run: RunWithDetails, username?: string): string {
    if (!run.ip_address) {
      return '';
    }
    
    const user = username || run.user.username;
    return `ssh ${user}@${run.ip_address}`;
  }
}

export const runService = new RunService();
